import React, { useMemo } from 'react';
import ResponsiveImage from '../../../responsive-image/ResponsiveImage';
import { Eye, Package, PenBox, Trash2, EyeOff } from 'lucide-react';
import {
  ProductInformation,
  ProductStatus,
} from '../../../../types/Products/product';
import { images } from 'ionic-ui';
import { formatPrice } from '../../../../utils/formatPrice';
import useProductCardActions from '../../../../hooks/useProductCardActions';
import { PRODUCTS_QUANTITY } from '../../../../Constants/productConstants';
import { ProductQuantity } from './ProductQuantity';
import applyDiscount from '../../../../utils/helperFunction/applyPriceDiscount';
import { Tag } from './Tag';

export type Props = {
  className?: string;
} & ProductInformation;

export default function ProductCard({ className = '', ...props }: Props) {
  const status: ProductStatus | undefined = useMemo(() => {
    if (!props.quantity) return 'out of stock';
    if (props.quantity <= PRODUCTS_QUANTITY.OUT_OF_STOCK) return 'out of stock';
    else if (props.quantity <= PRODUCTS_QUANTITY.LOW_ON_STOCK)
      return 'low stock';
    else if (props.discount_percentages.length > 0) return 'on sale';
    return 'in stock';
  }, [props.quantity]);

  const { onEdit, onRemove, onRestock, onUnlist } = useProductCardActions({
    id: props.id,
    productName: props.name,
  });

  return (
    <div
      className={`${className} p-4 rounded-sm border border-border flex flex-col w-full gap-3`}
    >
      <div className="w-full relative">
        {status !== 'in stock' && <Tag status={status} />}
        <ResponsiveImage
          className="rounded-sm"
          src={
            props.default_image_url ||
            props.images?.[0]?.file_url ||
            images.placeholder
          }
          aspectRatio={1}
        />
      </div>
      <div className="flex-1 space-y-2 w-full">
        <div className="space-y-1">
          <span className="block px-3 py-0.5 rounded-sm w-fit bg-customs-light-blue-foreground text-customs-light-blue font-semibold">
            <small>{props.category}</small>
          </span>
          <span className="block space-x-1">
            <small className="font-semibold high-emphasis-text">Tags:</small>
            {props.hash_tags.map((tag, idx) => (
              <small
                key={`tag-${idx}`}
                className="medium-emphasis-text font-medium"
              >
                #{tag.name}
              </small>
            ))}
          </span>
        </div>
        <h6 className="line-clamp-2 font-semibold high-emphasis-text first-letter:capitalize">
          {props.name}
        </h6>
      </div>
      <div className="space-y-3">
        <div>
          <p className=" font-medium space-x-2">
            <span className="text-customs-light-blue">
              {formatPrice(
                applyDiscount(
                  Number(props.price),
                  props.discount_percentages[0] || 0
                )
              )}
            </span>
            {props.discount_percentages[0] && (
              <small className="medium-emphasis-text line-through">
                {formatPrice(Number(props.price))}
              </small>
            )}
          </p>
          {props.discount_percentages?.[0] && (
            <small className="medium-emphasis-text font-semibold capitalize">
              {props.discount_percentages[0]}% discount applied
            </small>
          )}
        </div>
        {
          <ProductQuantity
            quantity={props.quantity || 0}
            save_as={props.save_as || 'archived'}
            status={status}
          />
        }
      </div>
      <div className="flex flex-wrap gap-2 w-full product-card-action-btns">
        {status === 'low stock' && (
          <button onClick={onRestock}>
            <span>
              <Package />
            </span>
            <span>Restock</span>
          </button>
        )}
        {status === 'out of stock' && (
          <button onClick={onRestock}>
            <span>
              <Package />
            </span>
            <span>Restock Now</span>
          </button>
        )}
        <button onClick={() => onEdit(props)}>
          <span>
            <PenBox
              className={`${!['in stock', 'on sale'].includes(status) && 'text-text-dark-high'}`}
            />
          </span>
          <span>Edit</span>
        </button>
        {['in stock', 'on sale'].includes(status) && (
          <button onClick={() => onUnlist(props.save_as)}>
            <span>{props.save_as === 'published' ? <EyeOff /> : <Eye />}</span>
            <span>{props.save_as === 'published' ? 'Unlist' : 'List'}</span>
          </button>
        )}
        {['in stock', 'on sale'].includes(status) && (
          <button onClick={() => onRemove()}>
            <Trash2 />
          </button>
        )}
      </div>
    </div>
  );
}
